import moment, { Moment } from 'moment';
import { SecurityCategory, CurrencyCode, SubmittedStatus, OrderStatus, ExchangeAgreementEnum, HSBCStatus, Repapering as RepaperingEnum } from './enums';

export type RBCDict = {
  '7AServer': string,
  DSOL: string,
  HOME: string,
  IB: string,
  LOGICALSESSID: string,
  NETA: string,
  RBCAPI: string,
  SGID: string,
  WM: string,
  language: string,
  rbPath: string,
  refId: string,
  requestTime: string,
  rollover: string,
  showlevel2: string,
  siteNavCss: string,
  siteNavJs: string,
  system: string,
  titleFooter: string,
  titleHeader: string,
  titleMenu: string,
  xbrand: string,
  xtid: string
};

export type Env = {
  RBA_DOMAIN: string,
  RBA_7ASERVER: string,
  RBA_URL: string,
  PUBLIC_URL: string,
  MOD_URL: string,
  API_URL: string,
  WEBUI_URL: string,
  ENVIRONMENT: string,
  ENABLE_ORDERSTATUS: boolean,
  ENABLE_RRIF: boolean,
  ENABLE_RRIFEDIT: boolean,
  RBA_SESSION_URL: string,
  IS_ISAM: string,
  ISAM_URL: string,
  ENABLE_MF_PRACTICE: boolean,
  ENABLE_EQ_MODIFY: boolean,
  ENABLE_EQ_CANCEL: boolean,
  MOD_TOKEN_REFETCH_INTERVAL: number,
  ENABLE_EQ_ETH: boolean,
  ENABLE_SYMBOLDETAILS_CHART: boolean,
  ENABLE_ISTRADABLE_FLAG_CHECK: boolean
};

export const defaultFeatures = {
  whatsnewalert: false,
  whatsnewversion: 0,
  // mindeventsvariant: 1
  emeraldphase: 0,
  onboarding: false,
  tours: false,
  globaltrading: false,
  globaltrading_hk: false,
  globaltrading_fr: false,
  globaltrading_de: false,
  globaltrading_gb: false,
  tranches: undefined,
  optionswebcomponent: false,
  gtmconsentscript: false
};
export type Features = typeof defaultFeatures;

export type NavigationOptions = {
  ReactEnabled: boolean
};

export type ServerDate = {
  serverDate: string
};

export type Account = {
  accountNumber: string,
  accountToken: string,
  accountName: string,
  accountCurrency: string,
  activeTrader: boolean,
  nickName: string,
  class: string,
  type: string,
  displayOrder: number,
  isSticky: boolean,
  isAllPractice: boolean,
  isAnyPractice: boolean,
  purpose: string,
  accountRelation: string,
  cashBalance: number,
  isVisible: boolean,
  supportedMarket: string[]
};

export type Tradecount = {
  tradeCounts: {
    [key: string]: number
  },
  asOfDate: string
};

export type SummaryAccountModel = {
  account: Account,
  balances: Array<{
    currency: string
    total: number
  }>,
  tradeCount: number,
  combinedTotalCad: number,
  combinedTotalUsd: number,
  exchangeRateUsd: number
};

export type BalanceSummary = {
  asOfDate: Moment,
  balanceSummaryModels: Array<SummaryAccountModel>,
  combineTotalOfAllAccountsinCad: number,
  combineTotalOfAllAccountsinUsd: number
};

export type SubTransaction = {
  orderDate: Moment,
  referenceId: string,
  status: SubmittedStatus,
  transferType: string,
  transferAmount: number,
  transferCurrency: string,
  toAccountNumber: string,
  toAccountCurrency: string,
  fromAccountNumber: string,
  fromAccountCurrency: string
};

export type ProcTransaction = {
  tradeDate: Moment,
  processingDate: Moment,
  settlementDate: Moment,
  activityType: string,
  symbol: string,
  accountNumber: string,
  amount: number,
  currency: string,
  companyName: string,
  quantity: number,
  unitPrice: number,
  nickName: string,
  accountName: string,
  accountClass: string,
  description: string,
  entryCodeWithDescription: string,
  market: string,
  adpSecurityNumber: string
};

export type Security = {
  descriptionEnglish: string,
  descriptionFrench: string,
  callOrPut?: 'Call' | 'Put',
  isOptionExpired?: boolean,
  securityId: {
    symbol: string,
    market: string,
    securityCategory: SecurityCategory
  }
};

export type Order = {
  orderId: string,
  action: string,
  status: OrderStatus,
  amount: number,
  quantity: number,
  filledQuantity: number,
  totalQuantity: number,
  orderType: string,
  transactionType: string,
  orderDate: Moment,
  lastChangeDate: Moment,
  goodThrough: Moment,
  duration: string,
  fillPrice: number,
  settlementCurrency: CurrencyCode,
  stopPrice: number,
  limitPrice: number,
  price: number,
  payCommissionWithPoints: boolean,
  specialInstruction: string,
  rejectCode: string,
  canEdit: boolean,
  canModify: boolean,
  canCancel: boolean,
  security: Security,
  switchSecurity?: Security,
  partialFills?: Array<{
    date: string | Moment,
    quantity: number,
    status: OrderStatus,
    fillPrice: number
  }>
};

export type OrderDetails = {
  asOfDate: Moment,
  orderDetailsModels: Array<Order>,
  account: Account & {
    tokens: {
      cad: string,
      usd: string
    }
  }
};

export type Quote = {
  name: string,
  market: string,
  symbol: string,
  lasttrade: Moment,
  lastprice: number,
  volume: number,
  changedollars: number,
  changepercent: number,
  currency: string,
  exchangename: string,
  exchangecode: string,
  previousCloseDate: Moment,
  isHoliday: boolean,
  askprice: number,
  asksize: number,
  bidprice: number,
  bidsize: number,
  isRealtime: boolean,
  isTradable: boolean,
  securityCategory: string,
  securityType: string,
  isoption: boolean,
  assetclass: string,
  issuetype: string,
  asOfDate: Moment,
  markethoursflag: 'InSession'| 'Before'|'After',
  symbolClass: string,
  extendedHoursQuote: {
    date: Date,
    price: number,
    volume: number,
    askDate: Date,
    askPrice: number,
    askSize: number,
    bidDate: Date,
    bidPrice: number,
    bidSize: number,
    todaysChange: number,
    todaysChangePercent: number,
    isRealtime: boolean,
    marketStatus: string,
    dataSource: string
  },
  option: {
    symbol: string,
    lastprice: number,
    changedollars: number,
    changepercent: number,
    askprice: number,
    asksize: number,
    bidprice: number,
    bidsize: number,
    date: string,
    strikeprice: number,
    callput: 'Call' | 'Put',
    isRealtime: boolean
  },
  mutualFund: {
    nav: string,
    yield: number,
    fiftyTwoWeekHigh: number,
    fiftyTwoWeekLow: number,
    minInitial: number,
    minSubsequent: number,
    load: string,
    rspEligible: string,
    rspMinInitial: number,
    rspMinSubsequent: number,
    totalAssets: number,
    category: string,
    merDate: string | Moment,
    mer: number,
    managementFee: number,
    turnoverRatio: number,
    sellOnly: boolean,
    closedToNewInvestors: boolean
  },
  optionExpiration?: boolean,
  boardLotSize?: number,
  isGemSecurity?:boolean,
};

export type CaseDetails = {
  caseID: string,
  createdDate: string,
  status: 'Received' | 'Pending' | 'Processed' | 'Rejected' | 'Unknown' | 'Error'
};

export type Rrif = {
  planYear: number,
  marketValueDate: Moment,
  marketValue: number,
  minAnnualPymnt: number,
  maxAnnualPymnt: number,
  eligibleAnnualPymnt: number,
  requestedAnnualPymnt: number,
  accntLegislation: string,
  federalTaxPerc: number,
  federalTaxAmt: number,
  provincialTaxPerc: number,
  provincialTaxAmt: number,
  nonResTaxPerc: number,
  nonResTaxAmt: number,
  paymentFrequency: string,
  altPaymentFrequency: Array<number>,
  firstPaymentDate: Moment,
  accountDetails: string,
  methodOfPayment: string,
  accountDetails2: string,
  methodOfPayment2: string,
  ytdPaymentGrossAmt: number,
  ytdExcessAmt: number,
  ytdFederalTaxAmt: number,
  ytdProvincialTaxAmt: number,
  ytdNonResTaxAmt: number,
  ytdPaymentNetAmt: number,
  displayInfoMsg: boolean,
  accountPurpose: string,
  canEdit: boolean,
  nickName: string,
  accountName: string,
  accountNumber: string,
  grossNetIndicator: 'G' | 'N',
  nextPaymentAmt: number,
  caseDetails?: CaseDetails,
  recentlySubmitted: boolean,
  isPaymentCompleted: boolean,
  enableElectedAnnualAmt: boolean
};

export type Address = {
  streetNoAndName: string,
  aptSuite: string,
  city: string,
  province: string,
  postalCode: string,
  country: string
};

export type AddressDetails = Address & { suite: string, streetNoOrName: string }

export type FormProfile = {
  FormTemplate: string,
  ProfileRequest: Profile
};

export type Profile = {
  firstName: string,
  lastName: string,
  middleInitial: string,
  dateOfBirth: Moment,
  maritalStatusCD: number & string,
  maritalStatus: string,
  isMarriedOrCommonLaw: boolean & string,
  primaryPhone: string,
  secondaryPhone: string,
  busPhone: string,
  ext: string,
  attorneyPrimaryPhone: string,
  attorneySecondaryPhone: string,
  attorneySecondaryPhoneExt: string,
  email: string,
  confirmEmail: string,
  homeAddress: Address,
  isMailingAddressSameAsHome: boolean & string,
  mailingAddress: Address,
  employerAddress: Address,
  attorneyHomeAddress: Address,
  coApplicantFirstName: string,
  coApplicantInitial: string,
  coApplicantLastName: string,
  attorneyFirstName: string,
  attorneyInitial: string,
  attorneyLastName: string,
  attorneyDateOfBirth: Moment,
  attorneyIsMarriedOrCommonLaw: boolean & string,
  attorneyRelationship: string,
  signingOfficerRelationship: string,
  isFamilyMember: boolean & string,
  specifyFamilyMember: string,
  specifyRelationship: string,
  isChargingFee: boolean & string,
  hasFinancialInterest: boolean & string,
  isLicensedToProvideAdvice: boolean & string,
  generalPowerAttorney: boolean & string,
  beneficialOwnerCountry: string,
  clientAuth: boolean & string,
  attorneyAuth: boolean & string,
  isRBCClient: boolean & string,
  financialInstitution: string,
  rbcClientCard: number & string,
  transitNumber: number & string,
  hasTradingAuthority: boolean & string,
  tradingAuthorityAccount: number & string,
  bankAccountNumber: number & string,
  spouseFirstName: string,
  spouseInitial: string,
  spouseLastName: string,
  annualIncome: string,
  annualIncomeCD: number & string,
  netLiquidAssets: number & string,
  netFixedAssets: number & string,
  totalAssets: number & string,
  occupation: string,
  occupationCD: number,
  employmentStatusCD: number & string,
  employmentStatus: string,
  spouseEmploymentStatusCD: number & string,
  spouseEmploymentStatus: string,
  attorneyEmploymentStatusCD: number & string,
  attorneyEmploymentStatus: string,
  attorneyOccupation: string,
  attorneyOccupationCD: number & string,
  attorneyOccupationOtherDesc: string,
  occupationOtherDesc: string,
  businessType: string,
  businessTypeCD: number & string,
  businessTypeOtherDesc: string,
  employerName: string,
  attorneyEmployerName: string,
  isHavingTAAccount: boolean & string,
  isDueForKYC: boolean & string,
  spouseOccupation: string,
  spouseOccupationCD: number & string,
  spouseOccupationOtherDesc: string,
  spouseBusinessType: string,
  spouseBusinessTypeCD: number & string,
  spouseBusinessTypeOtherDesc: string,
  spouseEmployerName: string,
  transit: string,
  accountNo: string,
  isCanadianCitizen: boolean & string,
  sin: string,
  isUSCitizen: boolean & string,
  ssn: string,
  proTraderStatus: boolean & string,
  memberFirmName: string,
  applicantInsiderIndicator: boolean & string,
  applicantInsiderText: string,
  spouseInsiderIndicator: 'yes' | 'no' | 'na',
  spouseInsiderText: string,
  applicantControlIndicator: boolean & string,
  applicantControlText: string,
  caseDetails: CaseDetails,
  hasActiveEligibleAccount: boolean & string,
  hasCanadianAddress: boolean & string,
  isValidSin: boolean & string,
  eligibleAccounts: Array<eligibleAccountsDetails>,
  accountSelected: string,
  ackOaa: boolean & string,
  ackConsent: boolean & string,
  ackOma: boolean & string,
  ackNoChange: boolean & string,
  ackBorrow: boolean & string,
  ackInfoChange: boolean & string,
  hasPastTrading: boolean & string,
  hasPlanTrading: boolean & string,
  isConvertAccount: boolean,
  IsAddressUpdated: boolean,
  IsSINUpdated: boolean,
  IsFinancialUpdated: boolean,
  IsEmploymentUpdated: boolean,
  IsThirdPartyIndividual: boolean & string,
  thirdPartyFirstName: string,
  thirdPartyInitial: string,
  thirdPartyLastName:string,
  thirdPartyDateOfBirth: Moment,
  thirdPartyRelationship: string,
  thirdPartyAddress: Address,
  thirdPartyPhone: string,
  thirdPartyPhoneExt: string,
  thirdPartyOccupation: string,
  thirdPartyBusinessName: string,
  thirdPartyPlaceOfIncorporation: string,
  thirdPartyIncorporationNumber: string,
  tpAcknowledgement: boolean & string,
  thirdPartyEmploymentStatus: string,
  thirdPartyEmploymentStatusCD: number & string,
  thirdPartyOccupationCD: number & string,
  thirdPartyEmployerName: string,
  formTemplate: string,
  isNameChange: boolean & string,
  isAddressChange: boolean & string,
};

export type ProfileClient = {
  firstName: string,
  lastName: string,
  middleInitial: string,
  linkedServices: LinkedService,
  royalCircle: boolean,
  hashId: string,
  hsbcStatusId: HSBCStatus,
  activeTrader: string,
  isHVC: boolean
}

export type TrancheMetadata = {
  trancheID: string,
  startDate: Moment | string,
  isSubmitted: boolean,
  currentTime: Moment | string
}

export type Documentdata = {
  id: string,
  code: string,
  nameEn: string,
  nameFr: string
}

export type DocumentRequest = {
  id: string,
  documents: Documentdata[]
}

export type LinkedService = {
  investing?: boolean,
  banking?: boolean,
  investEase?: boolean,
  ds?: boolean,
  wm?: boolean
  phn?: boolean,
  et?: boolean
}

export type LinkedServiceMapping = {
  [Item in keyof LinkedService]: [string, string?, string?];
} & {
  gam: [string, string?, string?];
};

export type Option = {
  code: string,
  description: string
};

export type eligibleAccountsDetails = {
  accountNumber: string,
  accountType: string,
  prodCat: string,
  caseDetails: CaseDetails
}

export type AvailableFunds = {
  timestamp: string | Moment,
  accountNumber: string,
  usd: number,
  cad: number,
  type: string
};

export type Holdings = {
  account: Account,
  positions: Array<{
    currency: string,
    adpCode: string,
    symbol: string,
    underlyingSymbol: string,
    market: string,
    description: string,
    descriptionFrench: string,
    adpSecurityNumber: string,
    quantity: number,
    previousDayQuantity: number,
    marketValue: number,
    marketValueInCAD: number,
    bookValue: number,
    bookValueInCAD: number,
    investmentCategory: string,
    errorSupCode: number,
    quotePreviousPrice: number,
    changeInDollars: number,
    changeInPercent: number,
    loadType: string,
    rspEligibility: string,
    automaticInvestmentPlan: string,
    annualDividendAmount: number,
    openInterest: number,
    couponRate: number,
    maturityDate: Date,
    divExDate: Date,
    expirationDate: Date,
    isDripEligible: boolean,
    securitySize: number,
    settledQuantity: number,
    memoHolderCode1: string,
    memoHolderCode2: string,
    memoHolderCode3: string,
    securityToken: string,
    nonNegotiable: boolean,
    isIntraday: boolean,
    isIntradayQuoteAvailable: boolean,
    isRealTimeQuotable: boolean,
    valuationDate: null,
    lastPrice: number,
    exchangeRateUsd: number,
    realTime: boolean,
    averageCost: number,
    unrealizedGainLoss: number,
    unrealizedGainLossPercent: number
  }>,
  asOfDate: string | Moment
};

export type TradeCommission = {
  securityCategory: string,
  actionType: string,
  amountType: string,
  amount: number,
  market: string,
  symbol: string,
  accountToken: string,
  dollarsIncludingCommission: number,
  dollarsExcludingCommission: number,
  commission: number,
  estimated: boolean,
  fundCurrency: string,
  estimatedCost: number,
  quantity: number,
  stockActionType: string,
  optionActionType: string,
  priceType: string,
  limitPriceValue: number,
  stopPriceValue: number
};

export type TradeOrder = {
  state: string,
  status: string,
  transactionId: string,
  orderId: string,
  currentDateTime: string,
  orderDateTime: string,
  currency: string,
  symbolDescription?: string,
  symbolDescriptionFrench?: string,
  fromSymbolDescription?: string,
  fromSymbolDescriptionFrench?: string,
  toSymbolDescription?: string,
  toSymbolDescriptionFrench?: string,
  load: 'FrontLoad' | 'BackLoad' | 'NoLoad' | 'NotApplicable',
  minimumInitial: number,
  minimumSubsequent: number,
  market: string,
  estimatedCost: number,
  commission: number,
  dollarsIncludingCommission: number,
  dollarsExcludingCommission: number,
  estimated: false,
  isFundFactDelivered: boolean,
  notes1: string,
  notes1French: string,
  notes2: string,
  notes3: string,
  toSymbolNotes1?: string,
  toSymbolNotes1French?: string
  request: {
    currentOrder: {
      actionType: string,
      quantity: number,
      amount: number,
      amountType: string,
      symbol: string,
      fromAccount: string,
      selectedAccountCurrency: string,
      contactNumber: string,
      duration: string,
      selectedSwitchSecuritySymbol?: string
    },
    transactionId: string,
    state: string,
    channel: string,
    device: string
  },
  fundFactDetails: {
    fundFactURL: string,
    fundPreviewURL: string,
    fundFactDocId: string,
    isFundFactDelivered: false,
    isFundFactAcknowledged: false
  },
  existingOrder: {
    orderId: string,
    orderDate: string,
    referenceKey: string,
    status: OrderStatus,
    order: TradeOrder['request']['currentOrder']
  },
  hasWarning: false,
  hasError: true,
  validationResult: Array<{
    id: number,
    severity: string,
    key: string,
    errorMessage: string,
    tag: string,
    description: string,
    priority: number
  }>,
  errors: Array<{
    id: string,
    message: string,
    translations: {
      Online: {
        en: string,
        fr: string
      }, Mobile: {
        en: string,
        fr: string
      }
    } | null
  }>,
  translations?: {
    Online?: {
      en: string,
      fr: string
    }, Mobile?: {
      en: string,
      fr: string
    }
  } | null
};

export type EquityOrder = TradeOrder & {
  estimatedPoints: number,
  estimatedCost: number,
  request: {
    currentOrder: {
      quantity: number,
      priceType: string,
      stopPriceValue: number,
      market: string,
      specialInstruction: string,
      isPayWithPoints: boolean,
      pointsAccount: string,
      goodThroughType: string,
      goodThroughDate: Date,
      limitPriceValue: number
    }
  },
  symbolDescription: string
};

type TradeFilterBase = {
  isEnabledForMarketPrice: boolean,
  isEnabledForStopPrice: boolean,
  isEnabledForStopLimitPrice: boolean,
  isEnabledForAllOrNoneSpecialInstruction: boolean,
  isEnabledForGoodThrough: boolean,
  isEnabled: boolean,
  isEnabledForCancel: boolean
};

export type TradeFilters = {
  equityFilter: {
    equityFilters: Record<'CA'|'US', TradeFilterBase & {
      isEnabledForBuy: boolean,
      isEnabledForSell: boolean,
      isEnabledForShortSell: boolean,
      isEnabledForCoverShort: boolean,
    }>,
  },
  optionFilter: {
    optionFilters: Record<'CA'|'US', TradeFilterBase & {
      isEnabledForBuyToOpen: boolean,
      isEnabledForSellToClose: boolean,
      isEnabledForBuyToClose: boolean,
      isEnabledForSellToOpen: boolean,
    }>,
  },
  mutualFundFilters: {
    isMFOrderEntryEnabled: boolean,
    isMFBuyEnabled: boolean,
    isMFSaleEnabled: boolean,
    isMFSwitchEnabled: boolean
  }
};

export type SwitchSecurityList = {
  securityList: Array<{
    symbol: string,
    descriptionEnglish: string,
    descriptionFrench: string,
    market: string
  }>
};

export type PredictiveSearchList = {
  security: {
      country: string,
      securityCategory: string,
      securityType: string,
      issueType: string,
      symbol: string,
      name: string,
      exchangeDisplay: string,
      exchangeName: string
  },
  quote: Quote,
  trendChart: {
    url: string,
    isValid: boolean,
    chartDimensions: {
      x: string,
      y: string,
      width: string,
      height: string
    }
  },
  symbolClass: string
};

export type RecentSymbolList = PredictiveSearchList & {
  insertDateTime: Date
};

export type GoodThroughDates = {
  CA: Array<string>,
  US: Array<string>
};

export type RewardPoints = {
  accountId: string,
  rewardAccountId: string,
  productNameEN: string,
  productNameFR: string,
  pointsBalance: number,
  conversionRate: number
};

export type LevelTwo = {
  ask: Array<{
    marketMaker: string,
    price: number,
    size: number,
    time: string
  }>,
  bid: Array<{
    marketMaker: string,
    price: number,
    size: number,
    time: string
  }>
};

export type StockOrder = {
  items: Array<{
    fillPrice: number,
    rejectCode: string,
    orderDate: Date,
    orderId: string,
    descriptionEnglish: string,
    descriptionFrench: string,
    status: string,
    orderDetails: {
        actionType: string,
        quantity: number,
        priceType: string,
        stopPriceValue: number,
        market: string,
        specialInstruction: string,
        isPayWithPoints: boolean,
        pointsAccount: number,
        goodThroughType: string,
        goodThroughDate: string,
        limitPriceValue: number
        symbol: string,
        fromAccount: string,
        selectedAccountCurrency: string,
        contactNumber: string,
        estimatedVal: number,
        duration: string
    }
  }>
};

export type Overview = {
    security:{
      country: string,
      issueType: string,
      symbol: string,
      name: string,
      exchangeDisplay: string,
      exchangeName: string
    },
    quoteDetails:{
      boardLotSize: number;
      currency: string,
      priceLast: number,
      priceLastClose: number,
      priceChange: number,
      priceChangePercent: number,
      priceAsk: number,
      priceBid: number,
      sizeAsk: number,
      sizeBid: number,
      priceOpen: number,
      volume: number,
      volumeAverage: number,
      volumePaceTick: number,
      priceFiftyTwoWeekHigh: number,
      priceFiftyTwoWeekLow: number,
      priceOneDayHigh: number,
      priceOneDayLow: number,
      marketCap: number,
      peRatio: number,
      priceDividendLast: number,
      dripEligible: boolean,
      dividendYieldPercent: number,
      dividendFrequencyName: string,
      dividendCurrency: string,
      dividendFrequency: number,
      dateDividendEx: Date,
      dateDividendNext: Date,
      dateEarningsNext: Date,
      dateLastClose: Date,
      datePriceLast: Date,
      totalNetAssets: number,
      category: string,
      inSession: number,
      realTime: number,
      netAssetValue: number,
      navAsOfDate: Date,
      navFiftyTwoWeekHigh: number,
      navFiftyTwoWeekLow: number,
      totalAssets: number,
      load: string,
      inception: Date,
      minInitial: number,
      minSubsequent: number,
      rspEligible: boolean,
      rspMinInitial: number,
      rspMinSubsequent: number
    },
    feesAndExpense:{
      smartText: string,
      mer: number,
      merDate: Date,
      merType: string,
      managementFee: number,
      turnoverRatio: number
    },
    companyInfo:{
      industryName: string,
      sectorName: string
    }
};

export type ClientDetails = {
    level2Quotes: boolean,
    lineOfBusiness: string,
    activeTrader: boolean,
    userTier: string,
    timeStamp: string
};

export type OptionsExpirations = {
    underlyingSecurity: {
      country: string,
      securityCategory: string,
      securityType: string,
      symbol: string,
      name: string,
      exchangeDisplay: string,
      exchangeName: string,
      exchangeCode: string
    },
    optionExpirations: Array<{
      adjusted: boolean,
      dateExpiration: Date,
      period: string,
      type: string
    }>
};

export type ExtTradingHoursUSCA = {
  US:{
    PreMarket:
      Array<{
       exchangeCode : {
          isEnabled: string,
          openTime: number,
          closeTime: number
        }
      }>,
    PostMarket: Array<{
      exchangeCode: {
        isEnabled: string,
        openTime: number,
        closeTime: number
      }
    }>,
  },
  CA:{
    PostMarket:{
      ALL:{
        isEnabled: string,
        openTime: number,
        closeTime: number
      }
    }
  }
}
export type ExtTradingHours = {
  PreMarket:{
    isEnabled: boolean,
    openTime: number,
    closeTime: number
  },
  PostMarket:{
    isEnabled: boolean,
    openTime: number,
    closeTime: number
  }
}
export type MODTokenDetails = boolean;

export type UserEmailPreference = {
  email: string,
  preferenceInvestmentProducts: boolean,
  preferenceDirectInvestingUpdates: boolean,
  preferenceProgramOffers: boolean,
  preferenceNewsKnowledge: boolean
};

export type showIntercept = {
  showIntercept: boolean,
  emailAddress?: string
};

export type ExchangeAgreement = {
  agreementType: ExchangeAgreementEnum,
  isAccepted: boolean,
  isNewAgreement: boolean
};

export type ExchangeAgreementOPRA = {
  isNonProfessional: boolean,
  acceptTermsandConditions: boolean
};

export type ExchangeAgreementNYSE = {
  subscriberTitle: string,
  subscriberEmployerFunction: string,
  answer_A: boolean,
  answer_B: boolean,
  answer_C: boolean,
  answer_D: boolean,
  answer_E: boolean,
  answer_F: boolean,
  answer_G: boolean,
  answer_H: boolean,
  answer_I: boolean,
  answer_J: boolean,
  answer_K: boolean,
  isNonProfessional: boolean,
  isAcceptTCNonProfessinal: boolean,
  acceptTermsandConditions: boolean
};

export type ExchangeAgreementNASDAQ = {
  typeOfSubscriber: number,
  subscriberStatus: number,
  isProfessional?: boolean,
  isBusiness?: boolean,
  firmName: string,
  individualSubscriberInfo?: string,
  firmNameOfSignatory?: string,
  firmTitle: string,
  subscriberSign: string,
  address: string,
  date: string,
  acceptTermsandConditions: boolean
};

export type OnboardingStatus = {
  status: {
    interactiveTour: {
      featuredTour: boolean,
      learntoTrade: boolean,
      accountManagement: boolean,
      investingPreferences: boolean,
      research: boolean,
      incomeProjection: boolean
    }
  }
};

export namespace RepaperingTypes {
  export type ClientAccountRelationship =
  {
    ctUserID: string,
    firstName: string,
    lastName: string,
    companyName: string,
    clientTypeId: string,
    relationshipType: RepaperingEnum.AccountRelationshipType,
  }
  export type Address =
  {
    streetNoAndName: string,
    aptSuite: string,
    city: string,
    province: string,
    country: string,
    countryCode: string,
    postalCode: string,
  }
  export type SupplementFormInfo =
  {
    trackingID: string,
    countryOfResidence1: string,
    countryOfResidence2: string,
    countryOfResidence3: string,
    iSOCountryOfResidence1: string,
    iSOCountryOfResidence2: string,
    iSOCountryOfResidence3: string,
    taxIdNumber1: string,
    taxIdNumber2: string,
    taxIdNumber3: string,
    taxTreatyCountry: string,
    taxTreatyISOCountry: string,
    agreementAccepted: boolean,
  }
  export type Form =
  {
    formID: RepaperingEnum.FormID,
    formName: LocalizedProperty,
    formUrl: LocalizedProperty,
    formType: RepaperingEnum.FormType,
    caseDetails: CaseDetails,
  }

  export type CaseDetails =
  {
    caseId: string,
    caseStatus: string,
  }

  export type LocalizedProperty =
  {
    en: string,
    fr: string,
  }

  export type SLUIStatus = {
    status: {
      digital?: RepaperingEnum.FormStatus,
      paper?: RepaperingEnum.FormStatus
    }
  }

  export type RawCustomerInfo =
  {
    firstName: string,
    middleInitial: string,
    lastName: string,
    ctUserID: string,
    loginClientCard: string,
    diClientCard: string,
    bankClientCard: string,
    dateOfBirthString: string,
    dateOfBirth: string,
    emailAddress: string,
    telephoneNo: string,
    phoneTypeCode: string,
    phoneDeviceType: string,
    phoneExtension: string,
    isMailingAddressSameAsHome: boolean,
    homeAddress: Address,
    mailingAddress: Address,
    maritalStatus: RepaperingEnum.MaritalStatus,
    isMarriedOrCommonLaw: boolean,
    isCoApplicantMySpouse?: boolean,
    canadianCitizenIndicator: boolean | string,
    usCitizenIndicator: boolean | string,
    sin: string | null,
    spousalSIN: string,
    spousalDateOfBirthString: string,
    spousalDateOfBirth?: string,
    countryOfBirth: string,
    ssn: string | null,
    sinExpiryDateString: string,
    sinExpiryDate?: string,
    language: RepaperingEnum.Language,
    spouseFirstName: string,
    spouseLastName: string,
    occupationCD: number,
    occupationDesc: string,
    employerName: string,
    employmentStatusCD: string,
    employmentStatusDesc: string,
    spouseOccupationCD: number,
    spouseOccupationDesc: string,
    spouseEmployerName: string,
    spouseEmploymentStatusCD: string,
    spouseEmploymentStatusDesc: string,
    netLiquidAssets?: number,
    netFixedAssets?: number,
    totalAssets?: number,
    annualIncome?: number | string,
    annualIncomeDesc: string,
    proTraderStatus: boolean | string,
    memberFirmName: string,
    applicantInsiderIndicator: boolean | string,
    applicantInsiderInfo: string,
    applicantControlIndicator: boolean | string,
    applicantControlInfo: string,
    consentToProfile: boolean,
    consentToShare: boolean,
    agreedToPrepop: boolean,
    consentToETax: boolean | string,
    docRequestId: string,
    documentType: DocumentType,
    consentToETaxAccepted: boolean,
    forms: Array<Form>,
    supplementFormInfo: RepaperingTypes.SupplementFormInfo,
    hasPastTrading?: boolean,
    hasPlanTrading?: boolean
  }

  export type CustomerInfo = RawCustomerInfo & SLUIStatus

  export type RawAccountInfo =
  {
    accountNumber: string,
    digitallySignedDateString: string,
    digitallySignedDate?: string,
    trackingID: string,
    accountType: RepaperingEnum.AccountType,
    accountPurposeDetail: RepaperingEnum.AccountPurposeDetail,
    prodCat: string,
    sourceOfFundsCD: number,
    sourceOfFundsDesc: string,
    coApplicantSourceOfFundsCD: number,
    coApplicantSourceOfFundsDesc: string,
    coApplicantClientCardNumber: string,
    dRIPIndicator: boolean,
    physicalMail: RepaperingEnum.PhysicalMail,
    disclosure: boolean,
    mailingCost: boolean,
    optionTrading: boolean,
    nakedPut: boolean,
    financialInterest: boolean,
    clientAccountAgreementAccepted: boolean,
    pensionLegislation: RepaperingEnum.PensionLegislation,
    pensionLegislationDesc: string,
    pensionOnSex?: boolean,
    clientTypeId: string,
    clientAccountRelationships: Array<ClientAccountRelationship>,
    forms: Array<Form>,
  }

  export type AccountInfo = RawAccountInfo & SLUIStatus & {
    accountName: string,
    coApps: ClientAccountRelationship[];
  }
}

export type RepaperingRaw =
{
  repaperingDetails: {
    customerInfo: RepaperingTypes.RawCustomerInfo,
    accountInfo: Array<RepaperingTypes.RawAccountInfo>,
  },
  isSubmitted: boolean
}

export type Repapering =
{
  repaperingDetails: {
    customerInfo: RepaperingTypes.CustomerInfo,
    accountInfo: Array<RepaperingTypes.AccountInfo>,
    isDigitalComplete?: boolean;
    isPaperComplete?: boolean;
    isPaperReceived?: boolean;
    isDigitalNigo?: boolean;
    isPaperNigo?: boolean;
  },
  isSubmitted: boolean
}

export type DestinationsControls = {
  tours: boolean,
  emeraldalert: boolean,
  olbcardlogin: boolean
}
